/**
 * Модель навигации: единственный источник правды и для верхних табов, и
 * для нижнего таб-бара, и для списка «иммерсивных» экранов.
 *
 * Пункты описаны данными, а не разметкой: `AppHeader` и `BottomNav` —
 * два разных вида одной и той же навигации, и держать её в двух местах
 * значило бы гарантированный рассинхрон (пункт добавили в один, забыли в
 * другом). Здесь — один список, который оба вида только рисуют.
 *
 * Иммерсивные маршруты (приветствие, онбординг, диагностика, урок) хрома
 * не получают вовсе: учебный поток не должен отвлекаться на навигацию, а
 * приветствие и онбординг вообще предшествуют «настоящему» приложению.
 * Признак вынесен в чистую функцию — её видно в тесте и не нужно
 * повторять в JSX.
 */

import { IconBrain, IconCards, IconHome, IconLayoutGrid, IconUser, type AppIcon } from '../components/icons';

/** Пункт навигации: куда ведёт, как называется и чем помечен. */
export interface NavItem {
  readonly to: string;
  /** Ключ подписи: `nav.<labelKey>` в словарях интерфейса. */
  readonly labelKey: 'home' | 'pocket' | 'learn' | 'services' | 'profile';
  readonly icon: AppIcon;
  /** Главное действие: стоит в центре и выделено кругом. */
  readonly primary?: true;
}

/**
 * Пять пунктов — предел нижнего бара без бургера: на 360px это по 72px
 * на пункт, подпись в одно слово ещё помещается.
 * Порядок задаёт и порядок в шапке, и порядок в таб-баре. «Учиться» —
 * главное действие — ровно посередине, третьим из пяти.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'home', icon: IconHome },
  { to: '/pocket', labelKey: 'pocket', icon: IconCards },
  { to: '/map', labelKey: 'learn', icon: IconBrain, primary: true },
  // Чат — один из сервисов: вкладка ведёт ко всем инструментам сразу.
  { to: '/services', labelKey: 'services', icon: IconLayoutGrid },
  { to: '/profile', labelKey: 'profile', icon: IconUser },
];

/** Маршруты, которые рендерятся без хрома — точно, а не по префиксу. */
const IMMERSIVE_ROUTES: ReadonlySet<string> = new Set([
  '/',
  '/onboarding',
  '/aptitude',
  '/call',
  '/login',
]);

/** Учебные экраны с параметром в пути: урок `/lesson/:skillId` и закрепление `/practice/:kind`; документы — тоже без хрома. */
const IMMERSIVE_PREFIXES: readonly string[] = ['/lesson/', '/practice/', '/legal/'];

/**
 * Экраны без входа: приветствие, онбординг (гость проходит его до
 * регистрации), вход и документы, на которые ссылается вход. Всё остальное —
 * только с аккаунтом: без него маршрут уводит на вход.
 */
const PUBLIC_ROUTES: ReadonlySet<string> = new Set(['/', '/onboarding', '/login']);

export function isPublicPath(pathname: string): boolean {
  return PUBLIC_ROUTES.has(pathname) || pathname.startsWith('/legal/');
}

/** Экран, где идут ответы: урок любого вида. Здесь работает квокка-тренер. */
export function isPracticePath(pathname: string): boolean {
  return pathname.startsWith('/lesson/') || pathname.startsWith('/practice/');
}

/**
 * Иммерсивный ли маршрут: без шапки и таб-бара. Учебные экраны несут
 * параметр (`/lesson/:skillId`), поэтому проверяются префиксом.
 */
export function isImmersivePath(pathname: string): boolean {
  if (IMMERSIVE_ROUTES.has(pathname)) return true;
  return IMMERSIVE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
